import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  APPROVAL_POLL_MS,
  ApiError,
  INITIAL_APPROVAL,
  api,
  defaultDeviceName,
  detectPlatform,
  isValidCode,
  onDeviceNotApproved,
  readPlatformEnv,
  reduceApproval,
  type DeviceApproval,
  type DeviceInfo,
} from "@helm/core";
import { ApproveDeviceScreen } from "./ApproveDeviceScreen";
import { loadStoredDevice, saveStoredDevice } from "./deviceStore";

/** The API calls the gate needs (injectable for the dev preview). */
export type DeviceGateApi = Pick<
  typeof api,
  "meta" | "registerDevice" | "listDevices" | "approveDevice" | "logout"
>;

function signedOut(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : "request failed";
}

type Registered = { device: DeviceInfo; approval: DeviceApproval; enforced: boolean };

let inflight: Promise<Registered> | null = null;

/**
 * Register or re-attach this device. Single-flight: concurrent callers (React StrictMode runs
 * effects twice in development) share one request, so a browser never registers two devices.
 */
function registerThisDevice(deviceApi: DeviceGateApi): Promise<Registered> {
  inflight ??= (async () => {
    const [meta, stored] = await Promise.all([deviceApi.meta(), loadStoredDevice()]);
    const env = readPlatformEnv();
    const res = await deviceApi.registerDevice({
      platform: detectPlatform(env),
      name: defaultDeviceName(env),
      ...(stored ? { device_id: stored.id, device_secret: stored.secret } : {}),
    });
    if (res.device_secret) await saveStoredDevice({ id: res.device.id, secret: res.device_secret });
    return {
      device: { id: res.device.id, name: res.device.name, platform: res.device.platform },
      approval: res.approval,
      enforced: meta.device_approval_required ?? true,
    };
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

/**
 * After sign-in: register (or re-attach) this device, then render `children` only once the
 * server says the device may be used (SPEC §20). While approval is pending, shows the
 * full-screen approval flow and polls. A later 403 DEVICE_NOT_APPROVED from any call sends the
 * app back here; a 401 (session or device revoked) reloads to the sign-in screen.
 */
export function DeviceGate({
  children,
  deviceApi = api,
}: {
  children: React.ReactNode;
  deviceApi?: DeviceGateApi;
}): React.JSX.Element {
  const [state, dispatch] = useReducer(reduceApproval, INITIAL_APPROVAL);
  const deviceRef = useRef<DeviceInfo | null>(null);

  useEffect(
    () =>
      onDeviceNotApproved(() => {
        dispatch({ type: "not_approved" });
      }),
    [],
  );

  // Register / re-attach whenever the machine is (back) in `registering`.
  useEffect(() => {
    if (state.phase !== "registering") return;
    const run = { live: true };
    registerThisDevice(deviceApi)
      .then((r) => {
        deviceRef.current = r.device;
        if (run.live) dispatch({ type: "registered", ...r });
      })
      .catch((e: unknown) => {
        if (signedOut(e)) location.reload();
        else if (run.live) dispatch({ type: "register_failed", message: message(e) });
      });
    return () => {
      run.live = false;
    };
  }, [state.phase, deviceApi]);

  // Poll while waiting: approved from another device, or the first-device code no longer applies.
  const waiting = state.phase === "code" || state.phase === "trusted";
  useEffect(() => {
    if (!waiting) return;
    const poll = (): void => {
      const me = deviceRef.current?.id;
      deviceApi
        .listDevices()
        .then((list) => {
          const mine = list.find((d) => d.id === me);
          dispatch({
            type: "poll",
            approved: mine?.approved === true,
            otherApproved: list.some((d) => d.id !== me && d.approved),
          });
        })
        .catch((e: unknown) => {
          if (signedOut(e)) location.reload();
        });
    };
    const t = setInterval(poll, APPROVAL_POLL_MS);
    return () => {
      clearInterval(t);
    };
  }, [waiting, deviceApi]);

  const submitCode = useCallback(
    (code: string) => {
      dispatch({ type: "code_submitted", code });
      const id = deviceRef.current?.id;
      if (!id || !isValidCode(code)) return;
      deviceApi
        .approveDevice(id, code)
        .then(() => {
          dispatch({ type: "code_accepted" });
        })
        .catch((e: unknown) => {
          if (signedOut(e)) location.reload();
          const err = e instanceof ApiError ? e : null;
          dispatch({
            type: "code_rejected",
            status: err?.status ?? 0,
            code: err?.code ?? "NETWORK",
            message: message(e),
          });
        });
    },
    [deviceApi],
  );

  if (state.phase === "approved") return <>{children}</>;
  if (state.phase === "registering") {
    return (
      <div className="centered muted" role="status">
        Checking this device…
      </div>
    );
  }
  return (
    <ApproveDeviceScreen
      state={state}
      onSubmitCode={submitCode}
      onNewCode={() => {
        dispatch({ type: "retry" });
      }}
      onRetry={() => {
        dispatch({ type: "retry" });
      }}
      onSignOut={() => {
        void deviceApi.logout().finally(() => {
          location.reload();
        });
      }}
    />
  );
}
